<template>
  <div>
    <q-toolbar color="white" text-color="grey-9" class="top-page-toolbar">
      <q-toolbar-title class="q-body-2">
        <router-link class="text-grey-10 no-underline" :to="{ name: 'time-tracking' }">
          {{ $t('time_tracking') }}
        </router-link> / <span class="text-weight-bold">{{ $t('report') }}</span>
      </q-toolbar-title>

      <q-btn icon="save" flat @click="handleExportTimeReport" :disable="!hasTimeEntries">
        <q-tooltip>{{ $t('export_timereport') }}</q-tooltip>
      </q-btn>

      <q-btn flat>
        {{ dateRangeHumanReadable }}
        <time-range-popover v-model="range" />
      </q-btn>

      <q-btn :label="$t('filter')" icon="filter_list" flat>
        <q-chip v-if="filterCount > 0" color="primary" floating>{{ filterCount }}</q-chip>
        <q-popover>
          <q-list separator link>
            <q-item>
              <q-item-side icon="person" />
              <q-item-main>
                <q-select
                  v-model="filter.userId"
                  :options="userIdFilterOptions"
                  :placeholder="$t('time_tracking_filter_user_id')"
                  hide-underline
                  clearable
                />
              </q-item-main>
            </q-item>
            <q-item>
              <q-item-side icon="folder" />
              <q-item-main>
                <project-select
                  v-model="filter.projectId"
                  :placeholder="$t('time_tracking_filter_project_id')"
                  hide-underline
                  clearable
                />
              </q-item-main>
            </q-item>
            <q-item>
              <q-item-side icon="attach_money" />
              <q-item-main>
                <q-select
                  v-model="filter.billed"
                  :options="billedFilterOptions"
                  :placeholder="$t('time_tracking_filter_billed')"
                  hide-underline
                  clearable
                />
              </q-item-main>
            </q-item>
            <q-item>
              <q-item-side icon="contacts" />
              <q-item-main>
                <multi-customer-select
                  v-model="filter.customer"
                  class="q-ma-none"
                />
              </q-item-main>
            </q-item>
          </q-list>
        </q-popover>
      </q-btn>
    </q-toolbar>
    <div class="layout-padding">
      <div class="row q-mb-lg">
        <div class="col-12 col-md-8">
          <time-entries-statistics-bar-chart
            v-if="hasTimeEntries && timeEntriesStatistics"
            :timeEntriesStatisticsData="timeEntriesStatistics.value"
            :height="250"
            :timeUnit="rangeUnit"
          />
        </div>
        <div class="col-12 col-md-4">
          <project-seconds-statistics-donut-chart
            v-if="hasTimeEntries && projectSecondsStatistics"
            :projectSecondsStatisticsData="projectSecondsStatistics.value"
            :options="{ legend: {  position: 'bottom' } }"
            :height="250"
          />
        </div>
      </div>

      <q-infinite-scroll ref="infiniteScroll" v-show="hasTimeEntries" :handler="handleFetchNextChunk">
        <time-entries-list :time-entries="timeEntries" :showUser="true"/>
      </q-infinite-scroll>

      <template v-if="!hasTimeEntries">
        <div class="row justify-center items-center col-grow q-mt-xl">
          <div class="text-center">
            <img src="~assets/emptystates/time_entries_empty.svg"/>

            <h6 class="q-my-md text-faded">{{ $t('no_time_entries_found') }}</h6>

            <span class="text-faded">{{ $t('change_filter_options') }}</span>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex'
  import FileSaver from 'file-saver'
  import MultiCustomerSelect from 'components/Common/Inputs/MultiCustomerSelect.vue'
  import ProjectIndicator from 'components/TimeTracking/ProjectIndicator.vue'
  import ProjectPopover from 'components/TimeTracking/ProjectPopover.vue'
  import ProjectSecondsStatisticsDonutChart from 'components/TimeTracking/ProjectSecondsStatisticsDonutChart.vue'
  import ProjectSelect from 'components/Common/Inputs/ProjectSelect.vue'
  import { StatisticsClient } from 'src/api/Statistics'
  import { TimeEntriesClient } from 'src/api/TimeEntries'
  import TimeEntriesList from 'components/TimeTracking/TimeEntriesList'
  import TimeEntriesStatisticsBarChart from 'components/TimeTracking/TimeEntriesStatisticsBarChart.vue'
  import TimeRangePopover from 'components/Common/Inputs/TimeRangePopover.vue'
  import { createErrorFromResponse } from 'src/utils/error'
  import { date } from 'quasar'
  import differenceInMonths from 'date-fns/difference_in_months'
  import isSameDay from 'date-fns/is_same_day'
  import parseDate from 'date-fns/parse'
  import startOfMonth from 'date-fns/start_of_month'
  import store from 'src/store'

  export default {
    components: {
      TimeEntriesList,
      ProjectIndicator,
      ProjectPopover,
      TimeRangePopover,
      TimeEntriesStatisticsBarChart,
      ProjectSecondsStatisticsDonutChart,
      ProjectSelect,
      MultiCustomerSelect
    },
    data () {
      return {
        filter: {
          userId: null,
          projectId: null,
          billed: null,
          customer: [],
          startedAtStart: startOfMonth(new Date()),
          startedAtEnd: new Date()
        },
        timeEntriesStatistics: null,
        projectSecondsStatistics: null
      }
    },
    computed: {
      ...mapGetters(['user', 'activeTeam', 'timeEntries']),
      range: {
        get () {
          return {
            start: this.filter.startedAtStart,
            end: this.filter.startedAtEnd
          }
        },
        set ({ start, end }) {
          this.filter.startedAtStart = start
          this.filter.startedAtEnd = end
        }
      },
      rangeUnit () {
        switch (differenceInMonths(this.range.end, this.range.start)) {
        case 0:
          return 'day'
        case 1: case 2: case 3: case 4: case 5:
          return 'week'
        default:
          return 'month'
        }
      },
      hasTimeEntries () {
        return !!this.timeEntries.length
      },
      filterForRequest () {
        let filter = []

        if (this.filter.userId !== null) {
          filter.push(['user_id', '=', this.filter.userId])
        }

        if (this.filter.projectId !== null) {
          filter.push(['project_id', '=', this.filter.projectId])
        }

        if (this.filter.customer.length > 0) {
          const filterValue = `{${this.filter.customer.map((customer) => customer.id).join(';')}}`
          filter.push(['customer_id', '=', filterValue])
        }

        if (this.filter.billed !== null) {
          filter.push(['billed', '=', this.filter.billed])
        }

        if (this.filter.startedAtStart !== null) {
          filter.push(['started_at', '>=', date.formatDate(parseDate(this.filter.startedAtStart), 'YYYY-MM-DD')])
        }

        if (this.filter.startedAtEnd !== null) {
          filter.push(['started_at', '<=', date.formatDate(parseDate(this.filter.startedAtEnd), 'YYYY-MM-DD')])
        }

        return filter
      },
      setFilters () {
        return Object.keys(this.filter)
          // Ignore started at filters because they are always present
          .filter((key) => !['startedAtStart', 'startedAtEnd'].includes(key))
          .filter((key) => Array.isArray(this.filter[key]) && this.filter[key].length !== 0)
          .filter((key) => this.filter[key] !== null)
      },
      filterCount () {
        return this.setFilters.length
      },
      userIdFilterOptions () {
        return this.activeTeam.members.map((member) => ({
          label: [member.first_name, member.last_name].join(' ').trim(),
          value: member.id
        }))
      },
      billedFilterOptions () {
        return [
          { label: this.$t('billed'), value: true },
          { label: this.$t('not_billed'), value: false }
        ]
      },
      dateRangeHumanReadable () {
        if (isSameDay(this.filter.startedAtStart, this.filter.startedAtEnd)) {
          return date.formatDate(parseDate(this.filter.startedAtStart), this.$t('date_format'))
        }

        return `${date.formatDate(parseDate(this.filter.startedAtStart), this.$t('date_format'))} - ${date.formatDate(parseDate(this.filter.startedAtEnd), this.$t('date_format'))}`
      }
    },
    methods: {
      ...mapActions([
        'listTimeEntries',
        'fetchNextTimeEntriesChunk'
      ]),
      updateRouteForFilter () {
        if (this.filterCount === 0) {
          const query = Object.assign({}, this.$route.query)
          delete query.f
          return this.$router.replace({ query })
        }

        const filter = JSON.stringify(this.filter)
        return this.$router.replace({ query: { ...this.$route.query, f: filter } })
      },
      parseFilterFromQuery () {
        if (Object.prototype.hasOwnProperty.call(this.$route.query, 'f')) {
          const {
            userId = null,
            projectId = null,
            customer = null,
            billed = null,
            startedAtStart = null,
            startedAtEnd = null
          } = JSON.parse(this.$route.query.f)

          this.filter.userId = userId
          this.filter.projectId = projectId
          this.filter.customer = customer
          this.filter.billed = billed
          this.filter.startedAtStart = parseDate(startedAtStart)
          this.filter.startedAtEnd = parseDate(startedAtEnd)
        }
      },
      handleRequest () {
        this.listTimeEntries({
          pagination: {
            include: [
              'user',
              'project',
              'customer'
            ],
            filterBy: this.filterForRequest
          }
        })
      },
      handleFetchNextChunk (index, done) {
        this.fetchNextTimeEntriesChunk({
          done: done,
          pagination: {
            include: [
              'user',
              'project',
              'customer'
            ],
            filterBy: this.filterForRequest
          }
        })
      },
      fetchStatistics () {
        new StatisticsClient()
          .filter(this.filterForRequest)
          .list({
            statistics: [
              {
                name: 'time_entries',
                options: {
                  unit: this.rangeUnit,
                  start_date: date.formatDate(parseDate(this.filter.startedAtStart), 'YYYY-MM-DD')
                }
              },
              {
                name: 'project_seconds',
                options: {
                  unit: 'month',
                  start_date: date.formatDate(parseDate(this.filter.startedAtStart), 'YYYY-MM-DD')
                }
              }
            ]
          })
          .then((response) => {
            this.timeEntriesStatistics = response.data.data.find((statistic) => statistic.name === 'time_entries')
            this.projectSecondsStatistics = response.data.data.find((statistic) => statistic.name === 'project_seconds')
          })
      },
      handleExportTimeReport () {
        let filter = {}

        if (this.filter.userId !== null) {
          filter['user_id'] = [this.filter.userId]
        }

        if (this.filter.projectId !== null) {
          filter['project_id'] = [this.filter.projectId]
        }

        if (this.filter.customer.length > 0) {
          filter['customer_id'] = this.filter.customer.map(customer => customer.id)
        }

        if (this.filter.billed !== null) {
          filter['billed'] = this.filter.billed
        }

        if (this.filter.startedAtStart !== null) {
          filter['start_date'] = date.formatDate(parseDate(this.filter.startedAtStart), 'YYYY-MM-DD')
        }

        if (this.filter.startedAtEnd !== null) {
          filter['end_date'] = date.formatDate(parseDate(this.filter.startedAtEnd), 'YYYY-MM-DD')
        }

        new TimeEntriesClient()
          .export(filter)
          .then(response => {
            const pdf = response.data
            const blob = new Blob([pdf], { type: 'application/pdf' })
            FileSaver.saveAs(blob, 'report.pdf')
          })
          .catch(createErrorFromResponse)
      }
    },
    watch: {
      filter: {
        handler: function () {
          this.updateRouteForFilter()
          this.handleRequest()
          this.fetchStatistics()
        },
        deep: true
      }
    },
    beforeRouteEnter (to, from, next) {
      let filter = []

      if (Object.prototype.hasOwnProperty.call(to.query, 'f')) {
        const {
          userId = null,
          projectId = null,
          customer = null,
          billed = null,
          startedAtStart = startOfMonth(new Date()),
          startedAtEnd = new Date()
        } = JSON.parse(to.query.f)

        if (userId !== null) {
          filter.push(['user_id', '=', userId])
        }

        if (projectId !== null) {
          filter.push(['project_id', '=', projectId])
        }

        if (customer.length > 0) {
          const filterValue = `{${customer.map((customer) => customer.id).join(';')}}`
          filter.push(['customer_id', '=', filterValue])
        }

        if (billed !== null) {
          filter.push(['billed', '=', billed])
        }

        if (startedAtStart !== null) {
          filter.push(['started_at', '>=', date.formatDate(parseDate(startedAtStart), 'YYYY-MM-DD')])
        }

        if (startedAtEnd !== null) {
          filter.push(['started_at', '<=', date.formatDate(parseDate(startedAtEnd), 'YYYY-MM-DD')])
        }
      } else {
        filter.push(['started_at', '>=', date.formatDate(startOfMonth(new Date()), 'YYYY-MM-DD')])
        filter.push(['started_at', '<=', date.formatDate(new Date(), 'YYYY-MM-DD')])
      }

      store.dispatch('listTimeEntries', {
        pagination: {
          include: [
            'user',
            'project',
            'customer'
          ],
          filterBy: filter
        }
      })
        .then(() => next((vm) => {
          vm.parseFilterFromQuery()
          vm.fetchStatistics()
        }))
    }
  }
</script>
